import { useEffect, useCallback, useRef, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  useAssessment,
  useAssessmentAttempts,
  useAttempt,
  useConnectedBook,
  useStartAttempt,
  useSubmitAnswer,
  useCompleteAttempt,
  useUpdateUserPreferences,
  useAssessmentAuthInfo,
  fetchAttempt,
} from "@/lib/api/assessments";
import { ApiError } from "@/lib/api";
import { FreeLoginFlow } from "@/components/auth";
import { useShallow } from "zustand/react/shallow";
import {
  useAssessmentStore,
  useCurrentQuestion,
  useProgress,
  useCanSubmit,
  useIsLastQuestion,
  useIsFirstQuestion,
} from "@/lib/stores/assessment";
import { useAuthStore } from "@/lib/stores/auth";
import { QuestionHeader } from "@/components/assessment/question-header";
import { QuestionContainer } from "@/components/assessment/question-container";
import { AssessmentResults } from "@/components/assessment/results";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Loader2Icon, PlayIcon } from "lucide-react";
import type { FeedbackState, AnswerRecord, UIAnswer } from "@/types/assessment";
import type { CodingLanguage, QuestionWithHiddenAnswer } from "@/types/api";
import {
  convertAnswerForAPI,
  isSingleChoiceHiddenAnswer,
  isMultiSelectHiddenAnswer,
} from "@/types/assessment";
import type { HLDWizardContent, HLDWizardResponse } from "@/types/hld-wizard";
import { isHLDWizardContent } from "@/types/hld-wizard";
import { isHLDWizardAllComplete } from "@/components/assessment/answer-input/hld-wizard";
import { useDraftAutoSave } from "@/lib/hooks/use-draft-auto-save";
import { useGetDrafts } from "@/lib/api/drafts";

// Supported question types for the UI
const SUPPORTED_QUESTION_TYPES = [
  "single_choice",
  "multi_select",
  "open_answer",
  "code_review",
  "refactor",
  "hld",
  "hld_wizard",
];

export function AssessmentTake() {
  const { id } = useParams<{ id: string }>();
  const assessmentId = parseInt(id || '0', 10);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Auth state - use individual selectors to avoid unnecessary re-renders
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const authLoading = useAuthStore((s) => s.isLoading);
  const fetchUser = useAuthStore((s) => s.fetchUser);

  // Note: initial fetchUser is handled by AuthProvider on app mount
  // fetchUser is still used here for the FreeLoginFlow onSuccess callback

  // Fetch auth info for this assessment (public endpoint, no auth required)
  // Only fetch when not authenticated - determines which login flow to show
  const { data: authInfo, isLoading: authInfoLoading } = useAssessmentAuthInfo(
    !isAuthenticated ? assessmentId : 0
  );

  // When authenticated, user has access (backend will enforce actual permissions)
  const hasAccess = isAuthenticated;

  // Assessment data - only fetch when authenticated with proper access
  const { data: assessment, isLoading: assessmentLoading } = useAssessment(
    hasAccess ? assessmentId : 0
  );

  // Fetch user's attempts for this assessment (newest first)
  const { data: attempts, isLoading: attemptsLoading } = useAssessmentAttempts(
    hasAccess ? assessmentId : 0
  );

  // Derive state from attempts (API returns newest first)
  const latestAttempt = attempts?.[0] ?? null;
  const hasInProgressAttempt = latestAttempt && !latestAttempt.completed_at;
  const hasCompletedAttempt = Boolean(latestAttempt?.completed_at);

  // Fetch full attempt with questions when resuming an in-progress attempt
  const { data: attemptWithQuestions, isLoading: attemptLoading } = useAttempt(
    hasInProgressAttempt ? latestAttempt.id : 0
  );

  // Fetch the completed attempt fresh so gap_analysis lands once the LLM has
  // populated it. Triggered only when the user has just completed an attempt
  // in this session (storeIsCompleted via the assessment store).
  // The same query key (`["attempt", id]`) is invalidated by useCompleteAttempt
  // and useRetryGapAnalysis, so this hook stays current.

  // Fetch drafts for in-progress attempt
  const { data: draftsData } = useGetDrafts(
    hasInProgressAttempt ? latestAttempt.id : null
  );

  // Mutations
  const startAttempt = useStartAttempt();
  const completeAttempt = useCompleteAttempt();
  const updatePreferences = useUpdateUserPreferences();

  // Store state - use shallow selector to batch related state
  const {
    storeAttemptId,
    storeAssessmentTitle,
    storeQuestions,
    storeCurrentAnswer,
    storeIsSubmitting,
    storeFeedback,
    storeShowFeedback,
    storeIsCompleted,
    storeFinalScore,
    storeSubmittedAnswers,
    storeIsReviewMode,
    storeHiddenAnswers,
    storeVideoExplanations,
  } = useAssessmentStore(
    useShallow((s) => ({
      storeAttemptId: s.attemptId,
      storeAssessmentTitle: s.assessmentTitle,
      storeQuestions: s.questions,
      storeCurrentAnswer: s.currentAnswer,
      storeIsSubmitting: s.isSubmitting,
      storeFeedback: s.feedback,
      storeShowFeedback: s.showFeedback,
      storeIsCompleted: s.isCompleted,
      storeFinalScore: s.finalScore,
      storeSubmittedAnswers: s.submittedAnswers,
      storeIsReviewMode: s.isReviewMode,
      storeHiddenAnswers: s.hiddenAnswers,
      storeVideoExplanations: s.videoExplanations,
    }))
  );

  // Store actions - keep as individual selectors (actions are stable)
  const initAssessment = useAssessmentStore((s) => s.initAssessment);
  const initReviewMode = useAssessmentStore((s) => s.initReviewMode);
  const setCurrentAnswer = useAssessmentStore((s) => s.setCurrentAnswer);
  const setSubmitting = useAssessmentStore((s) => s.setSubmitting);
  const setFeedback = useAssessmentStore((s) => s.setFeedback);
  const setHiddenAnswer = useAssessmentStore((s) => s.setHiddenAnswer);
  const setVideoExplanation = useAssessmentStore((s) => s.setVideoExplanation);
  const nextQuestion = useAssessmentStore((s) => s.nextQuestion);
  const previousQuestion = useAssessmentStore((s) => s.previousQuestion);
  const completeAssessmentAction = useAssessmentStore(
    (s) => s.completeAssessment
  );

  const currentQuestion = useCurrentQuestion();
  const progress = useProgress();
  const canSubmit = useCanSubmit();
  const isLastQuestion = useIsLastQuestion();
  const isFirstQuestion = useIsFirstQuestion();

  // Draft auto-save for complex question types
  const { status: draftStatus } = useDraftAutoSave(
    storeAttemptId,
    currentQuestion?.id ?? null,
    currentQuestion?.type,
    storeCurrentAnswer,
  );

  // Fetch completed attempt data for review mode (includes hidden_answer)
  // Must be after store selectors since it depends on storeAttemptId and storeIsReviewMode
  const { data: completedAttemptData, isLoading: completedAttemptLoading } = useAttempt(
    hasCompletedAttempt && !storeAttemptId && !storeIsReviewMode ? latestAttempt!.id : 0
  );

  // Fetch the just-completed attempt fresh so gap_analysis (populated server-side
  // synchronously by GapAnalysisService.GenerateGapAnalysisForAttempt) is on
  // hand when the report renders. The completion mutation invalidates this
  // query; the retry mutation also invalidates it so the report re-renders
  // after a manual retry.
  const { data: justCompletedData } = useAttempt(
    storeIsCompleted && storeAttemptId ? storeAttemptId : 0
  );

  // Fetch the assessment's book linkage. Drives the gap-analysis CTA variant
  // (free / paid-starting / silent). Returns null when the assessment isn't
  // book-linked. Only fetched when we're about to render the report.
  const { data: connectedBook } = useConnectedBook(assessmentId, {
    enabled: storeIsCompleted && Boolean(assessment?.blind_mode),
  });

  // Submit answer mutation - now takes attemptId in mutation variables (fix 1.5)
  const submitAnswer = useSubmitAnswer();

  // Track hidden answer for current question after submission
  const [currentHiddenAnswer, setCurrentHiddenAnswer] = useState<
    Record<string, unknown> | undefined
  >(undefined);

  // Vim mode - use user preference directly instead of duplicating state (fix 2.3)
  const vimEnabled = user?.vim_motions_enabled ?? false;

  // Redirect to Whop login when:
  // 1. User is not authenticated AND
  // 2. Assessment doesn't allow email auth (requires Whop/member login)
  // Note: Assessments with email auth show FreeLoginFlow instead
  useEffect(() => {
    if (authLoading || authInfoLoading) return;

    // If authenticated, no redirect needed
    if (isAuthenticated) return;

    // If auth info not loaded yet, wait
    if (!authInfo) return;

    // If email auth allowed, show FreeLoginFlow (handled in render)
    if (authInfo.allows_email_auth) return;

    // Otherwise redirect to Whop login
    const returnUrl = encodeURIComponent(pathname);
    navigate(`/login?returnUrl=${returnUrl}`);
  }, [
    authLoading,
    authInfoLoading,
    isAuthenticated,
    authInfo,
    navigate,
    pathname,
  ]);

  // Reset store when component unmounts or assessment changes
  useEffect(() => {
    return () => {
      useAssessmentStore.getState().reset();
    };
  }, [assessmentId]);

  // Auto-resume in-progress attempt when data is ready (fix 1.2 - stable dependencies)
  useEffect(() => {
    if (
      hasInProgressAttempt &&
      attemptWithQuestions &&
      assessment &&
      !storeAttemptId
    ) {
      // Extract hidden answers and video explanations from questions
      const hiddenAnswers: Record<number, Record<string, unknown>> = {};
      const videoExplanations: Record<number, string> = {};
      for (const q of attemptWithQuestions.questions ?? []) {
        const qWithHidden = q as QuestionWithHiddenAnswer;
        if (qWithHidden.hidden_answer) {
          hiddenAnswers[q.id] = qWithHidden.hidden_answer;
        }
        if (qWithHidden.video_explanation) {
          videoExplanations[q.id] = qWithHidden.video_explanation;
        }
      }

      // Build drafts map from API response
      const draftsMap: Record<number, unknown> = {};
      if (draftsData) {
        for (const d of draftsData) {
          draftsMap[d.question_id] = d.response;
        }
      }

      initAssessment(
        attemptWithQuestions.attempt.id,
        assessmentId,
        attemptWithQuestions.attempt.assessment_title ?? assessment.title,
        attemptWithQuestions.questions ?? [],
        attemptWithQuestions.answers ?? [],
        hiddenAnswers,
        draftsMap
      );

      // Set video explanations in store
      for (const [qId, url] of Object.entries(videoExplanations)) {
        setVideoExplanation(Number(qId), url);
      }
    }
  }, [
    hasInProgressAttempt,
    attemptWithQuestions,
    assessment,
    assessmentId,
    storeAttemptId,
    draftsData,
    initAssessment,
    setVideoExplanation,
  ]);

  // Initialize review mode for completed assessments
  useEffect(() => {
    if (
      hasCompletedAttempt &&
      completedAttemptData &&
      assessment &&
      !storeAttemptId &&
      !storeIsReviewMode
    ) {
      // Build hidden answers and video explanations from questions
      const hiddenAnswers: Record<number, Record<string, unknown>> = {};
      const videoExplanations: Record<number, string> = {};
      for (const q of completedAttemptData.questions ?? []) {
        const qWithHidden = q as QuestionWithHiddenAnswer;
        if (qWithHidden.hidden_answer) {
          hiddenAnswers[q.id] = qWithHidden.hidden_answer;
        }
        if (qWithHidden.video_explanation) {
          videoExplanations[q.id] = qWithHidden.video_explanation;
        }
      }

      initReviewMode(
        completedAttemptData.attempt.id,
        assessmentId,
        completedAttemptData.attempt.assessment_title ?? assessment.title,
        completedAttemptData.questions ?? [],
        completedAttemptData.answers ?? [],
        hiddenAnswers,
        completedAttemptData.attempt.score ?? 0
      );

      // Set video explanations in store
      for (const [qId, url] of Object.entries(videoExplanations)) {
        setVideoExplanation(Number(qId), url);
      }
    }
  }, [
    hasCompletedAttempt,
    completedAttemptData,
    assessment,
    assessmentId,
    storeAttemptId,
    storeIsReviewMode,
    initReviewMode,
    setVideoExplanation,
  ]);

  // Skip unsupported question types via effect instead of during render (fix 1.1)
  const hasSkippedRef = useRef(false);
  useEffect(() => {
    if (
      currentQuestion &&
      !SUPPORTED_QUESTION_TYPES.includes(currentQuestion.type) &&
      !hasSkippedRef.current
    ) {
      hasSkippedRef.current = true;
      nextQuestion();
    } else if (
      currentQuestion &&
      SUPPORTED_QUESTION_TYPES.includes(currentQuestion.type)
    ) {
      hasSkippedRef.current = false;
    }
  }, [currentQuestion, nextQuestion]);

  // Handle starting the assessment (new attempt only) - memoized (fix 2.2)
  const handleStart = useCallback(async () => {
    if (!assessment) return;

    try {
      const response = await startAttempt.mutateAsync(assessmentId);
      initAssessment(
        response.attempt.id,
        assessmentId,
        response.attempt.assessment_title ?? assessment.title,
        response.questions ?? [],
        []
      );
    } catch (error) {
      console.error("Failed to start assessment:", error);
      toast.error("Failed to start assessment. Please try again.");
    }
  }, [assessment, assessmentId, startAttempt, initAssessment]);

  // Handle answer submission - memoized (fix 2.2)
  const handleSubmit = useCallback(async () => {
    if (!currentQuestion || storeCurrentAnswer === null || !storeAttemptId)
      return;

    setSubmitting(true);

    try {
      const apiAnswer = convertAnswerForAPI(
        currentQuestion.type,
        storeCurrentAnswer
      );

      // Pass attemptId with mutation (fix 1.5)
      const response = await submitAnswer.mutateAsync({
        attemptId: storeAttemptId,
        question_id: currentQuestion.id,
        answer: apiAnswer,
      });

      const feedback: FeedbackState = {
        isCorrect: response.answer.is_correct,
        explanation: response.answer.feedback,
        correctOptions: extractCorrectOptions(response, currentQuestion.type),
      };

      const answerRecord: AnswerRecord = {
        questionId: currentQuestion.id,
        answer: storeCurrentAnswer,
        feedback,
        submittedAt: new Date(),
      };

      setFeedback(feedback, answerRecord);
      setCurrentHiddenAnswer(response.question.hidden_answer);
      // Also persist in store for navigation within session
      if (response.question.hidden_answer) {
        setHiddenAnswer(currentQuestion.id, response.question.hidden_answer);
      }
      if (response.question.video_explanation) {
        setVideoExplanation(currentQuestion.id, response.question.video_explanation);
      }
      // Blind-mode auto-advance: there's no per-question feedback to read,
      // so jump straight to the next question. nextQuestion() is a no-op
      // on the last question, leaving the user with the Complete button.
      if (assessment?.blind_mode) {
        nextQuestion();
      }
    } catch (error) {
      // 409 = answer already exists (e.g., network retry created it before we got the response)
      // Recover by fetching the existing attempt data
      if (error instanceof ApiError && error.status === 409 && storeAttemptId) {
        console.warn("Answer already submitted (409), recovering...");
        try {
          const attempt = await fetchAttempt(storeAttemptId);
          const existingAnswer = attempt.answers?.find(a => a.question_id === currentQuestion.id);
          if (existingAnswer) {
            const feedback: FeedbackState = {
              isCorrect: existingAnswer.is_correct,
              explanation: existingAnswer.feedback,
              correctOptions: [],
            };
            const answerRecord: AnswerRecord = {
              questionId: currentQuestion.id,
              answer: storeCurrentAnswer,
              feedback,
              submittedAt: new Date(),
            };
            setFeedback(feedback, answerRecord);

            // Find the question with hidden answer + video from the attempt response
            const questionWithHidden = attempt.questions.find(q => q.id === currentQuestion.id);
            if (questionWithHidden && 'hidden_answer' in questionWithHidden && questionWithHidden.hidden_answer) {
              setCurrentHiddenAnswer(questionWithHidden.hidden_answer);
              setHiddenAnswer(currentQuestion.id, questionWithHidden.hidden_answer);
            }
            if (questionWithHidden && 'video_explanation' in questionWithHidden && questionWithHidden.video_explanation) {
              setVideoExplanation(currentQuestion.id, questionWithHidden.video_explanation as string);
            }
            // Same blind-mode auto-advance as the success path. The 409
            // recovery means the answer was already on the server; we still
            // want the no-feedback flow.
            if (assessment?.blind_mode) {
              nextQuestion();
            }
          }
        } catch (recoveryError) {
          console.error("Failed to recover from 409:", recoveryError);
          toast.error("Failed to submit answer. Please try again.");
        }
      } else {
        console.error("Failed to submit answer:", error);
        toast.error("Failed to submit answer. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    currentQuestion,
    storeCurrentAnswer,
    storeAttemptId,
    submitAnswer,
    setSubmitting,
    setFeedback,
    setHiddenAnswer,
    setVideoExplanation,
    assessment?.blind_mode,
    nextQuestion,
  ]);

  // Handle moving to next question - memoized (fix 2.2)
  const handleNext = useCallback(() => {
    nextQuestion();
    // Update hidden answer for the new current question
    const nextIndex = useAssessmentStore.getState().currentIndex;
    const nextQ = useAssessmentStore.getState().questions[nextIndex];
    if (nextQ) {
      setCurrentHiddenAnswer(storeHiddenAnswers[nextQ.id]);
    }
  }, [nextQuestion, storeHiddenAnswers]);

  // Handle finishing the assessment
  const handleFinish = useCallback(async () => {
    // In review mode, just show results
    if (storeIsReviewMode) {
      completeAssessmentAction(storeFinalScore ?? 0);
      return;
    }
    // In taking mode, complete the attempt
    if (storeAttemptId) {
      try {
        const result = await completeAttempt.mutateAsync(storeAttemptId);
        completeAssessmentAction(result.score ?? 0);
      } catch (error) {
        console.error("Failed to complete assessment:", error);
        toast.error("Failed to complete assessment.");
        completeAssessmentAction(0);
      }
    }
  }, [
    storeAttemptId,
    storeIsReviewMode,
    storeFinalScore,
    completeAttempt,
    completeAssessmentAction,
  ]);

  // Handle moving to previous question - memoized
  const handlePrevious = useCallback(() => {
    previousQuestion();
    // Update hidden answer for the new current question
    const prevIndex = useAssessmentStore.getState().currentIndex;
    const prevQ = useAssessmentStore.getState().questions[prevIndex];
    setCurrentHiddenAnswer(prevQ ? storeHiddenAnswers[prevQ.id] : undefined);
  }, [previousQuestion, storeHiddenAnswers]);

  // Handle answer change - memoized (fix 2.2)
  const handleAnswerChange = useCallback(
    (answer: UIAnswer) => {
      setCurrentAnswer(answer);
    },
    [setCurrentAnswer]
  );

  // Handle vim toggle - memoized (fix 2.2)
  const handleVimToggle = useCallback(
    (enabled: boolean) => {
      updatePreferences.mutate({ vim_motions_enabled: enabled });
    },
    [updatePreferences]
  );

  // Handle language save - memoized (fix 2.2)
  const handleLanguageSave = useCallback(
    (lang: string) => {
      updatePreferences.mutate({ default_coding_language: lang as CodingLanguage });
    },
    [updatePreferences]
  );

  // Loading states
  if (authLoading || (!isAuthenticated && authInfoLoading)) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2Icon className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Show FreeLoginFlow when not authenticated AND email auth is allowed
  if (!isAuthenticated && authInfo?.allows_email_auth) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <FreeLoginFlow
          assessmentId={assessmentId}
          onSuccess={async () => {
            // Refresh auth state - React will re-render with new state
            await fetchUser();
          }}
        />
      </div>
    );
  }

  // Redirect pending - show loading while useEffect navigates to Whop login
  // This prevents "Assessment not found" flash before redirect completes
  if (!isAuthenticated && authInfo && !authInfo.allows_email_auth) {
    return (
      <div className="flex items-center justify-center min-h-[400px] gap-3">
        <Loader2Icon className="size-8 animate-spin text-muted-foreground" />
        <span className="text-muted-foreground">Redirecting to login...</span>
      </div>
    );
  }

  // Still loading assessment data after auth
  if (assessmentLoading || attemptsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2Icon className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Loading when resuming an in-progress attempt
  if (hasInProgressAttempt && attemptLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] gap-3">
        <Loader2Icon className="size-8 animate-spin text-muted-foreground" />
        <span className="text-muted-foreground">Resuming assessment...</span>
      </div>
    );
  }

  // Assessment not found
  if (!assessment) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">Assessment not found.</p>
            <Button
              variant="outline"
              onClick={() => navigate("/dashboard")}
              className="mt-4"
            >
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading review mode data for completed attempt
  if (hasCompletedAttempt && !storeAttemptId && !storeIsReviewMode) {
    if (completedAttemptLoading) {
      return (
        <div className="flex items-center justify-center min-h-[400px] gap-3">
          <Loader2Icon className="size-8 animate-spin text-muted-foreground" />
          <span className="text-muted-foreground">Loading review...</span>
        </div>
      );
    }
    // If we have data but review mode hasn't initialized yet, show loading
    // The useEffect will initialize review mode
    return (
      <div className="flex items-center justify-center min-h-[400px] gap-3">
        <Loader2Icon className="size-8 animate-spin text-muted-foreground" />
        <span className="text-muted-foreground">Preparing review mode...</span>
      </div>
    );
  }

  // Show results if just completed (store tracks completion during this session)
  if (storeIsCompleted) {
    const isBlind = assessment?.blind_mode ?? false;
    const justCompletedAttempt = justCompletedData?.attempt;

    // Blind mode: report renders directly on cream — no max-w wrapper, the
    // GapAnalysisReport owns its own width. Non-blind: existing graded
    // scorecard inside the page wrapper.
    if (isBlind) {
      return (
        <AssessmentResults
          title={storeAssessmentTitle}
          score={storeFinalScore ?? 0}
          totalQuestions={storeQuestions.length}
          submittedAnswers={storeSubmittedAnswers}
          onBackToDashboard={() => navigate("/dashboard")}
          blindMode
          gapAnalysis={justCompletedAttempt?.gap_analysis}
          attempt={justCompletedAttempt}
          connectedBook={connectedBook ?? null}
        />
      );
    }
    return (
      <div className="max-w-2xl mx-auto p-6">
        <AssessmentResults
          title={storeAssessmentTitle}
          score={storeFinalScore ?? 0}
          totalQuestions={storeQuestions.length}
          submittedAnswers={storeSubmittedAnswers}
          onBackToDashboard={() => navigate("/dashboard")}
          blindMode={false}
        />
      </div>
    );
  }

  // Show start screen if no attempt exists yet
  if (!storeAttemptId && !hasInProgressAttempt) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle>{assessment.title}</CardTitle>
            {assessment.description && (
              <CardDescription>{assessment.description}</CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-sm text-muted-foreground">
              Answer each question before proceeding. After answering, you can
              navigate back to review your previous answers.
            </p>

            <Button
              onClick={handleStart}
              disabled={startAttempt.isPending}
              size="lg"
              className="w-full"
            >
              {startAttempt.isPending ? (
                <>
                  <Loader2Icon className="size-4 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <PlayIcon className="size-4" />
                  Start Assessment
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show current question
  if (!currentQuestion) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">No questions available.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Skip rendering while auto-skipping unsupported question types (fix 1.1)
  if (!SUPPORTED_QUESTION_TYPES.includes(currentQuestion.type)) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2Icon className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Get user's default language preference
  const userDefaultLanguage = user?.default_coding_language;

  // For hld_wizard, canSubmit requires ALL decisions to be complete
  const effectiveCanSubmit = currentQuestion.type === "hld_wizard"
    ? canSubmit && isHLDWizardAllComplete(
        storeCurrentAnswer as HLDWizardResponse | null,
        currentQuestion.content as unknown as HLDWizardContent,
      )
    : canSubmit;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      <QuestionHeader
        title={storeAssessmentTitle}
        current={progress.current}
        total={progress.total}
      />

      <QuestionContainer
        question={currentQuestion}
        answer={storeCurrentAnswer}
        onAnswerChange={handleAnswerChange}
        onLanguageSave={handleLanguageSave}
        onVimToggle={handleVimToggle}
        onSubmit={handleSubmit}
        onNext={handleNext}
        onPrevious={handlePrevious}
        onFinish={handleFinish}
        isSubmitting={storeIsSubmitting}
        canSubmit={effectiveCanSubmit}
        feedback={storeFeedback}
        showFeedback={storeShowFeedback}
        isLastQuestion={isLastQuestion}
        isFirstQuestion={isFirstQuestion}
        isReviewMode={storeIsReviewMode}
        hiddenAnswer={currentHiddenAnswer ?? (currentQuestion ? storeHiddenAnswers[currentQuestion.id] : undefined)}
        videoExplanation={currentQuestion ? storeVideoExplanations[currentQuestion.id] : undefined}
        userDefaultLanguage={userDefaultLanguage}
        userVimEnabled={vimEnabled}
        blindMode={assessment?.blind_mode ?? false}
      />
    </div>
  );
}

// Helper to extract correct options from submit response.
// Tolerates blind-mode responses where hidden_answer and is_correct are both absent
// (returns undefined cleanly).
function extractCorrectOptions(
  response: {
    answer: { is_correct?: boolean; feedback?: string; answer: unknown };
    question: { type: string; hidden_answer?: Record<string, unknown> };
  },
  questionType: string
): string[] | undefined {
  const hiddenAnswer = response.question.hidden_answer;

  if (hiddenAnswer) {
    if (
      questionType === "single_choice" &&
      isSingleChoiceHiddenAnswer(hiddenAnswer)
    ) {
      return [String(hiddenAnswer.correct_option_id)];
    }
    if (
      questionType === "multi_select" &&
      isMultiSelectHiddenAnswer(hiddenAnswer)
    ) {
      return hiddenAnswer.options
        .filter((opt) => opt.is_correct)
        .map((opt) => String(opt.option_id));
    }
  }

  if (response.answer.is_correct) {
    const submittedAnswer = response.answer.answer;
    if (questionType === "single_choice" && typeof submittedAnswer === "number") {
      return [String(submittedAnswer)];
    }
    if (questionType === "multi_select" && Array.isArray(submittedAnswer)) {
      return submittedAnswer.map((id) => String(id));
    }
  }

  return undefined;
}
